import {
  FinancialTransaction,
  FestivalPledge,
  ExpenseRequisition,
  DepartmentBudget,
  FinancialAsset,
  CHART_OF_ACCOUNTS
} from '../types';
import { getRaffleTickets } from '../components/festival/RaffleHub';

const FINANCIAL_DATA_KEY = 'uzairue_financial_management_v1';
export const FINANCIAL_DATA_CHANGE_EVENT = 'uzairue_financial_data_updated';

export interface FinancialDataStore {
  transactions: FinancialTransaction[];
  pledges: FestivalPledge[];
  requisitions: ExpenseRequisition[];
  budgets: DepartmentBudget[];
  assets: FinancialAsset[];
  botApprovalThreshold: number; // e.g., 500000
  lastUpdated: string;
  updatedBy: string;
}

export const INITIAL_DEPARTMENT_BUDGETS: DepartmentBudget[] = [
  { code: '50-100-001', departmentName: 'Logistics & Stage Engineering', allocatedAmount: 15000000, spentAmount: 4200000, committedAmount: 1500000 },
  { code: '50-100-002', departmentName: 'Cultural Troupes & Artiste Honoraria', allocatedAmount: 10000000, spentAmount: 2500000, committedAmount: 800000 },
  { code: '50-100-003', departmentName: 'Security Escorts & Health Emergency', allocatedAmount: 5000000, spentAmount: 1200000, committedAmount: 300000 },
  { code: '50-100-004', departmentName: 'Media, Gazette & Publicity', allocatedAmount: 4000000, spentAmount: 1800000, committedAmount: 500000 },
  { code: '50-100-005', departmentName: 'Royal Protocol & VIP Hospitality', allocatedAmount: 8000000, spentAmount: 3100000, committedAmount: 1200000 },
  { code: '50-100-006', departmentName: 'ERUEWin Raffle Star Prize Procurement', allocatedAmount: 35000000, spentAmount: 18500000, committedAmount: 0 },
  { code: '50-100-007', departmentName: 'General Secretarial & Admin', allocatedAmount: 3000000, spentAmount: 850000, committedAmount: 150000 },
];

export const INITIAL_TRANSACTIONS: FinancialTransaction[] = [
  {
    id: 'tx-1001',
    date: '2026-07-15',
    description: 'Seed Royal Endowment Donation by HRH Ogieneni of Uzairue',
    amount: 10000000,
    type: 'donation',
    category: 'Philanthropy',
    accountCode: '40-100-001',
    status: 'cleared',
    referenceNo: 'ROYAL-DON-2026-001',
    payerOrPayee: 'HRH Ogieneni of Uzairue Kingdom',
    paymentMethod: 'bank_transfer',
    voucherNo: 'REC-2026-001',
    createdBy: 'High Chief Aliu Momoh (BoT Treasurer)',
    approvedBy: 'Board of Trustees',
    notes: 'Royal seed endowment for festival preparation and youth fund'
  },
  {
    id: 'tx-1002',
    date: '2026-07-20',
    description: 'Deposit for ERUEWin SUV Star Prize Vehicle Acquisition',
    amount: 15000000,
    type: 'expense',
    category: 'Raffle Star Prize Procurement',
    accountCode: '50-100-006',
    status: 'cleared',
    referenceNo: 'SUV-PROCUR-049',
    payerOrPayee: 'Innoson Vehicle Manufacturing Ltd / Dealer',
    paymentMethod: 'bank_transfer',
    voucherNo: 'PV-2026-014',
    createdBy: 'Engr. Abdulazeez Haruna (Chairman)',
    approvedBy: 'Board of Trustees (BoT Release)',
    notes: '50% initial downpayment for G80 SUV star prize for ERUEWin Raffle'
  },
  {
    id: 'tx-1003',
    date: '2026-07-28',
    description: 'USA Diaspora Union Chapter Pledge Fulfillment (Part 1)',
    amount: 5000000,
    type: 'pledge_redemption',
    category: 'Pledge Fulfillment',
    accountCode: '40-100-003',
    status: 'cleared',
    referenceNo: 'PLEDGE-RET-001',
    payerOrPayee: 'Uzairue Diaspora Association of North America',
    paymentMethod: 'bank_transfer',
    voucherNo: 'REC-2026-042',
    createdBy: 'Ikpemhi Oshioke (Financial Scribe)',
    approvedBy: 'Management Committee',
    notes: 'Partial redemption of $10,000 USD pledged for cultural stage'
  },
  {
    id: 'tx-1004',
    date: '2026-08-01',
    description: 'Stage Construction & Heavy Sound Rig Advance Payment',
    amount: 3500000,
    type: 'expense',
    category: 'Logistics & Stage Engineering',
    accountCode: '50-100-001',
    status: 'cleared',
    referenceNo: 'STAGE-LOG-2026-A',
    payerOrPayee: 'Royal Sound & Stage Services Benin',
    paymentMethod: 'bank_transfer',
    voucherNo: 'PV-2026-019',
    createdBy: 'Chief E. A. Afegbua (Logistics Lead)',
    approvedBy: 'Board of Trustees',
    notes: 'Contract payment for grand arena stage construction'
  },
  {
    id: 'tx-1005',
    date: '2026-08-05',
    description: 'Bulk Paystack Souvenir & Cap Store Direct Sales Revenue',
    amount: 1850000,
    type: 'store_sale',
    category: 'Store Sales',
    accountCode: '40-100-004',
    status: 'cleared',
    referenceNo: 'PAYSTACK-AGG-0805',
    payerOrPayee: 'Paystack Festival Online Store Gateways',
    paymentMethod: 'paystack_card',
    voucherNo: 'REC-2026-088',
    createdBy: 'Paystack Automated Webhook Sync',
    approvedBy: 'System Auto-Reconciliation',
    notes: 'Online sales for royal caps, festival brochures, and cultural wristbands'
  }
];

export const INITIAL_PLEDGES: FestivalPledge[] = [
  {
    id: 'plg-101',
    pledgerName: 'Uzairue Diaspora Association of North America',
    titleOrOffice: 'North America Chapter President',
    villageOrChapter: 'USA & Canada Chapter',
    pledgerType: 'diaspora_chapter',
    amountPledged: 10000000,
    amountRedeemed: 5000000,
    status: 'partially_redeemed',
    pledgeDate: '2026-06-15',
    dueDate: '2026-08-25',
    purpose: 'Sponsorship of 22 Village Youth Cultural Troupes & Sound Systems',
    contactEmail: 'diaspora.na@uzairue.org',
    contactPhone: '+1-202-555-0199',
    redemptionHistory: [
      {
        id: 'red-1',
        amount: 5000000,
        date: '2026-07-28',
        paymentMethod: 'Bank Wire Transfer',
        receiptNo: 'REC-2026-042',
        recordedBy: 'Ikpemhi Oshioke',
        bankRef: 'WIRE-US-993821'
      }
    ]
  },
  {
    id: 'plg-102',
    pledgerName: 'High Chief Dr. Raymond Afegbua',
    titleOrOffice: 'Oshiozekhai of Uzairue Kingdom',
    villageOrChapter: 'Afashio-Uzairue',
    pledgerType: 'royal_chief',
    amountPledged: 3000000,
    amountRedeemed: 3000000,
    status: 'fully_retired',
    pledgeDate: '2026-07-01',
    dueDate: '2026-08-01',
    purpose: 'Procurement of Standby Emergency Mobile Health Unit & Ambulances',
    contactEmail: 'chief.afegbua@uzairue.org',
    contactPhone: '+234-803-300-4411',
    redemptionHistory: [
      {
        id: 'red-2',
        amount: 3000000,
        date: '2026-08-02',
        paymentMethod: 'First Bank Direct Deposit',
        receiptNo: 'REC-2026-055',
        recordedBy: 'Ikpemhi Oshioke',
        bankRef: 'FBN-DEP-88402'
      }
    ]
  },
  {
    id: 'plg-103',
    pledgerName: 'Baze Energy & Oil Services Ltd',
    titleOrOffice: 'Managing Director',
    villageOrChapter: 'Jattu Commercial District',
    pledgerType: 'corporate_sponsor',
    amountPledged: 5000000,
    amountRedeemed: 0,
    status: 'outstanding',
    pledgeDate: '2026-07-10',
    dueDate: '2026-08-20',
    purpose: 'Corporate Co-Sponsorship of Festival Security Infrastructure & Lighting',
    contactEmail: 'sponsorships@bazeenergy.com',
    contactPhone: '+234-802-999-1122',
    redemptionHistory: []
  },
  {
    id: 'plg-104',
    pledgerName: 'London & UK Uzairue Sons & Daughters Union',
    titleOrOffice: 'United Kingdom Patron',
    villageOrChapter: 'UK & Europe Chapter',
    pledgerType: 'diaspora_chapter',
    amountPledged: 4500000,
    amountRedeemed: 2000000,
    status: 'partially_redeemed',
    pledgeDate: '2026-07-12',
    dueDate: '2026-08-28',
    purpose: 'Grant for 500 Traditional Royal Attires for Village Elders',
    contactEmail: 'uk.chapter@uzairue.org',
    contactPhone: '+44-20-7946-0921',
    redemptionHistory: [
      {
        id: 'red-3',
        amount: 2000000,
        date: '2026-08-08',
        paymentMethod: 'Online Paystack Portal Transfer',
        receiptNo: 'REC-2026-091',
        recordedBy: 'Ikpemhi Oshioke',
        bankRef: 'PSTK-UK-0021'
      }
    ]
  }
];

export const INITIAL_REQUISITIONS: ExpenseRequisition[] = [
  {
    id: 'req-201',
    requisitionNo: 'REQ-2026-001',
    title: 'Deployment of 120 Local Vigilantes & Police Escorts for Arena Security',
    department: 'Security & Health',
    requestedBy: 'Chief Security Officer (Commander Zuberu)',
    amount: 1200000,
    description: 'Daily stipends, uniforms, communication walkie-talkies and emergency logistics for 5 days of festival events.',
    accountCode: '50-100-003',
    status: 'bot_approved',
    priority: 'urgent',
    requiresBotApproval: true,
    createdDate: '2026-08-08',
    beneficiaryName: 'Uzairue Central Security & Vigilante Corps',
    bankAccountDetails: 'Zenith Bank - 1012938491',
    approvalHistory: [
      {
        id: 'app-1',
        step: 'Submission',
        actorName: 'Commander Zuberu',
        actorRole: 'Chief Security Officer',
        action: 'submitted',
        timestamp: '2026-08-08 10:30 AM',
        notes: 'Submitted for immediate approval due to security mobilization lead time.'
      },
      {
        id: 'app-2',
        step: 'Management Review',
        actorName: 'Engr. Abdulazeez Haruna',
        actorRole: 'Management Committee Chairman',
        action: 'reviewed',
        timestamp: '2026-08-08 02:15 PM',
        notes: 'Budget verified under 50-100-003. Passed to Board of Trustees due to threshold.'
      },
      {
        id: 'app-3',
        step: 'Board of Trustees Approval',
        actorName: 'High Chief Aliu Momoh',
        actorRole: 'BoT Treasurer / Royal Council Lead',
        action: 'approved',
        timestamp: '2026-08-09 09:00 AM',
        notes: 'Board of Trustees hereby authorizes full requisition of N1,200,000.'
      }
    ]
  },
  {
    id: 'req-202',
    requisitionNo: 'REQ-2026-002',
    title: 'Honoraria Grants for 22 Village Performing Cultural Troupes',
    department: 'Cultural Troupes',
    requestedBy: 'Cultural Director (Chief M. Iyora)',
    amount: 2200000,
    description: 'Mobilization grants of N100,000 to each of the 22 villages for troupe costume preparation and transportation.',
    accountCode: '50-100-002',
    status: 'mc_reviewed',
    priority: 'normal',
    requiresBotApproval: true,
    createdDate: '2026-08-10',
    beneficiaryName: '22 Village Daudus & Cultural Leads',
    bankAccountDetails: 'Multiple Village Accounts',
    approvalHistory: [
      {
        id: 'app-4',
        step: 'Submission',
        actorName: 'Chief M. Iyora',
        actorRole: 'Cultural Director',
        action: 'submitted',
        timestamp: '2026-08-10 11:00 AM',
        notes: 'Grants for traditional performers across Jattu, Afashio, Elele, etc.'
      },
      {
        id: 'app-5',
        step: 'Management Review',
        actorName: 'Ikpemhi Oshioke',
        actorRole: 'Financial Scribe',
        action: 'reviewed',
        timestamp: '2026-08-10 04:30 PM',
        notes: 'Cleared by Management Committee. Sent to Board of Trustees for final disbursement authorization.'
      }
    ]
  },
  {
    id: 'req-203',
    requisitionNo: 'REQ-2026-003',
    title: 'Printing of 5,000 High-Gloss Royal Commemorative Programs & Gazettes',
    department: 'Media & Publicity',
    requestedBy: 'Publications Committee Secretary',
    amount: 450000,
    description: 'Full color printing of official royal festival souvenir programs with gold foil embossing.',
    accountCode: '50-100-004',
    status: 'submitted',
    priority: 'normal',
    requiresBotApproval: false,
    createdDate: '2026-08-11',
    beneficiaryName: 'Uzairue Royal Press & Printworks',
    bankAccountDetails: 'First Bank - 3029102931',
    approvalHistory: [
      {
        id: 'app-6',
        step: 'Submission',
        actorName: 'Scribe Bureau',
        actorRole: 'Publications Lead',
        action: 'submitted',
        timestamp: '2026-08-11 01:20 PM',
        notes: 'Within N500,000 limit. Standard Management Committee approval required.'
      }
    ]
  }
];

export const INITIAL_ASSETS: FinancialAsset[] = [
  {
    id: 'ast-1',
    name: 'Uzairue Royal Stage & Modular Truss System (30m x 20m)',
    value: 8500000,
    acquiredDate: '2025-09-15',
    accountCode: '10-300-001',
    condition: 'excellent',
    location: 'Palace Central Warehouse, Jattu-Uzairue'
  },
  {
    id: 'ast-2',
    name: '1000KVA Ultra-Silent Generator & Sound Amplifiers',
    value: 12000000,
    acquiredDate: '2025-11-20',
    accountCode: '10-300-001',
    condition: 'good',
    location: 'Festival Main Ground Substation'
  },
  {
    id: 'ast-3',
    name: 'Innoson G80 Luxury SUV (ERUEWin Raffle Star Prize Asset)',
    value: 30000000,
    acquiredDate: '2026-07-20',
    accountCode: '10-300-001',
    condition: 'excellent',
    location: 'Royal Palace Display Pavilion'
  }
];

export function getFinancialStore(): FinancialDataStore {
  try {
    const raw = localStorage.getItem(FINANCIAL_DATA_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.transactions)) {
        return parsed;
      }
    }
  } catch (err) {
    console.error('Failed to load financial store from localStorage:', err);
  }
  return {
    transactions: INITIAL_TRANSACTIONS,
    pledges: INITIAL_PLEDGES,
    requisitions: INITIAL_REQUISITIONS,
    budgets: INITIAL_DEPARTMENT_BUDGETS,
    assets: INITIAL_ASSETS,
    botApprovalThreshold: 500000,
    lastUpdated: new Date().toISOString(),
    updatedBy: 'System Default'
  };
}

export function saveFinancialStore(store: FinancialDataStore, updatedBy = 'System Admin'): void {
  try {
    store.lastUpdated = new Date().toISOString();
    store.updatedBy = updatedBy;
    localStorage.setItem(FINANCIAL_DATA_KEY, JSON.stringify(store));
    
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(FINANCIAL_DATA_CHANGE_EVENT, { detail: store }));
    }

    // Sync to backend database
    fetch('/api/portal/financial/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(store),
    }).catch(() => {});
  } catch (err) {
    console.error('Failed to save financial store:', err);
  }
}

// Automatically sync raffle hub ticket revenue into financial transactions
export function syncRaffleRevenueToFinancial(): number {
  const store = getFinancialStore();
  const tickets = getRaffleTickets();

  let totalRaffleRev = 0;
  let paidTicketsCount = 0;

  tickets.forEach(t => {
    totalRaffleRev += (t.totalAmount || (t.quantity * 1000));
    paidTicketsCount += 1;
  });

  if (totalRaffleRev === 0) return 0;

  // Check if a raffle sync transaction exists
  const existingIndex = store.transactions.findIndex(t => t.id === 'tx-raffle-auto-sync');
  const now = new Date().toISOString().split('T')[0];

  const syncTx: FinancialTransaction = {
    id: 'tx-raffle-auto-sync',
    date: now,
    description: `ERUEWin Raffle Ticket Sales Auto-Sync (${paidTicketsCount} Active/Drawn Tickets)`,
    amount: totalRaffleRev,
    type: 'raffle_sale',
    category: 'Raffle Ticket Sales',
    accountCode: '40-100-002',
    status: 'cleared',
    referenceNo: 'RAFFLE-LIVE-SYNC-2026',
    payerOrPayee: 'ERUEWin Ticket Sales Portal Hub',
    paymentMethod: 'paystack_card',
    voucherNo: 'REC-RAFFLE-AUTO',
    createdBy: 'ERUEWin Raffle Real-time System',
    approvedBy: 'Auto-Reconciliation Engine',
    notes: 'Live automated feed from ERUEWin Raffle Hub module'
  };

  if (existingIndex >= 0) {
    store.transactions[existingIndex] = syncTx;
  } else {
    store.transactions.unshift(syncTx);
  }

  saveFinancialStore(store, 'ERUEWin Raffle Auto-Sync');
  return totalRaffleRev;
}
